class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * Publishes the header's bottom edge as --header-bottom-position-desktop so the
 * mega-menu panel can size itself against the viewport.
 *
 * Shared by every header-menu on the page on purpose: there is one value and one
 * element to watch, so the observer is created once and reused. Without that guard
 * a six-item nav installs six live observers, five of them orphaned, all calling
 * getBoundingClientRect() on every resize tick. The old code wrote the property
 * once, behind `if (getPropertyValue(...) !== '') return`, and never again — with
 * `sticky_header_type: "reduce-logo-size"` the header shrinks once scrolled, so
 * from the first scroll onward every panel was capped against a stale, too-tall
 * header and could no longer use the space it had.
 *
 * A ResizeObserver on the header wrapper is the mechanism that actually covers the
 * cases, because the header's height changes for reasons that have nothing to do
 * with this code: the sticky transition, a late-loading logo, a web font swapping in.
 * The resize listener is the cheap backstop for the viewport-height change that a
 * ResizeObserver does not report when the element's box is unaffected.
 */
class HeaderBottomPosition {
  static get instance() {
    if (!this._instance) this._instance = new HeaderBottomPosition();
    return this._instance;
  }

  observe(header) {
    if (!header) return;
    this.header = header;
    this.measure();

    if (typeof ResizeObserver === 'function') {
      // Created once. observe() is idempotent for an already-observed element, so
      // calling it again is free and re-points the single observer if the header
      // wrapper was ever re-rendered, instead of stranding it on a detached node.
      if (!this.observer) this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(header);
    }

    if (!this.boundResize) {
      this.boundResize = () => this.measure();
      window.addEventListener('resize', this.boundResize, { passive: true });
    }
  }

  measure() {
    if (!this.header || !this.header.isConnected) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

// Key under which whichever <header-menu> moved focus first claims the keydown event.
// A <ul> holds several <header-menu> elements and each delegates to the same list, so
// one press reaches the handler once per instance; without this they would all act.
const ARROW_KEY_HANDLED = 'sierraArrowKeyHandled';

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    HeaderBottomPosition.instance.observe(this.header);

    this.summaryToggle = this.mainDetailsToggle.querySelector('summary');
    // Delegated to the nav list, not to this <details>. onArrowKey collects two kinds
    // of stop from the list: a disclosure <summary>, and a plain leaf
    // <a class="header__menu-item"> with no <details> ancestor. A per-<details>
    // listener left arrows dead the moment focus landed on a leaf link, so a keyboard
    // shopper could arrow along the menu and then get stuck on unresponsive keys.
    const arrowList = this.mainDetailsToggle.closest('ul');
    if (arrowList) arrowList.addEventListener('keydown', this.onArrowKey.bind(this));
  }

  /**
   * WAI-ARIA disclosure-navigation: Left/Right move between the header's top-level
   * items, Home/End jump to the ends. Both ends wrap, which is what the horizontal
   * pattern does; there is no up/down pair because the header menu is one row, and
   * fewer than two stops is not a menu, so a single-item nav is left alone.
   *
   * The stops are the <li>s of the <ul> that wraps this <header-menu> — the nearest
   * ancestor <ul>, one level above parentElement, which is this element. Both menu
   * types are collected: a <summary> trigger for mega and dropdown alike (the caret
   * is a child span; the summary itself is what takes focus), or the plain
   * <a class="header__menu-item"> for an entry with no children.
   *
   * The listener is delegated to that <ul>, not bound per <details>, because the two
   * kinds of stop are not focusable in the same place. A per-<details> listener only
   * ever saw a press that came from a disclosure trigger, which left a leaf <a> as a
   * stop the shopper could arrive at and then not leave with the arrow keys — dead
   * keys in the middle of the row. From the <ul>, the press arrives from whichever
   * stop holds focus, and that element — not this menu's own summary — is what says
   * where in the row focus is.
   *
   * A listener on the whole <ul> also covers everything else inside it, so two
   * guards keep that honest. A target that resolves to no stop returns early instead
   * of guessing an index — a link inside an open panel, a caret <span> with no stop
   * of its own, whatever a section drops in later. And the sibling <header-menu>
   * elements each register on the same <ul>, so one press reaches this method once
   * per instance; the first to move focus claims the event and the rest return, which
   * is what holds a press to exactly one stop.
   *
   * Deliberately not role="menu". These are disclosures whose panels are ordinary
   * lists of links, and a menu role would oblige us to handle arrows for every item
   * *inside* the panel too, plus the focus and typeahead model screen readers switch
   * into — for a list of category links, that is a downgrade.
   *
   * Tab is left exactly as it was. It is the pattern's own escape hatch and the only
   * route into a panel that does not require the shopper to know these keys exist, so
   * arrow keys are an addition rather than a replacement. Escape already closes,
   * wired by global.js:123-136 for these summaries because their <details> id starts
   * with "Details-".
   */
  onArrowKey(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    if (event[ARROW_KEY_HANDLED]) return;

    // parentElement is <header-menu> — this element. The <li> stops are one level
    // further out, so walk up to the nav's <ul> rather than off a level.
    const list = this.mainDetailsToggle.closest('ul');
    if (!list) return;

    // Sibling <li>s, each reduced to the one element a shopper can land on: the
    // disclosure trigger, or the plain link for a menu entry with no children.
    const stops = Array.from(list.children)
      .map(
        (item) =>
          item.querySelector(':scope > header-menu > details > summary') ||
          item.querySelector(':scope > a.header__menu-item')
      )
      .filter(Boolean);

    if (stops.length < 2) return;

    // Delegated, so this press may have come from anywhere in the <ul>, and the stop
    // it belongs to is whichever element actually has focus — this menu's own summary
    // is only one candidate, so reading that would answer every press with the same
    // index no matter where focus was. Matched by identity, not by walking up the
    // tree: anything that is not itself a stop (a link inside an open panel, a caret
    // <span>, whatever else a section puts in the list) is left to its own key
    // handling rather than being guessed at.
    const current = stops.indexOf(event.target);
    if (current === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % stops.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + stops.length) % stops.length;
        break;
      case 'Home':
        next = 0;
        break;
      default:
        next = stops.length - 1;
    }

    // One press, one stop. preventDefault also swallows the Home/End scroll, and
    // stops any UA arrow handling on the summary from adding a second move on top
    // of this one. The claim matters because every sibling <header-menu> registered
    // on this same <ul> would otherwise run this identical line again.
    event.preventDefault();
    event[ARROW_KEY_HANDLED] = true;
    stops[next].focus();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Re-measure on open as well as on resize: between two opens the header can have
    // changed height without the observer having been installed yet (the observer
    // starts when the first header-menu upgrades, which is after first layout).
    HeaderBottomPosition.instance.measure();
  }
}

customElements.define('header-menu', HeaderMenu);
